import { crearComboSeleccion } from '@/components/moleculas/Seleccion/combo';
import {
  CuentaGiro,
  TipoCuenta,
} from '@/components/paginas/Cliente/Operacion/Resumen/models/cuentaGiro.model';
import { COMBO_BANCOS, CUENTAS, TIPO_CUENTAS_COMBO } from '@/helpers/urls';
import { DatoSeleccion } from '@/models/componentes/select.model';
import RespuestaApi from '@/models/respuestas/resApi.model';
import api from '@/providers/api';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ACTIVO, CARGANDO, INACTIVO } from '../constantes';
import { mostrarAlertaError } from '@/helpers/alert';

export type DatosCuentasBancarias = {
  cuentasUsuario: CuentaGiro[];
  tiposCuenta: TipoCuenta[];
  comboBancos: DatoSeleccion[];
  estado: string;
};

const estadoInicial: DatosCuentasBancarias = {
  cuentasUsuario: [],
  tiposCuenta: [],
  comboBancos: [],
  estado: INACTIVO,
};

// Obtener las cuentas del usuario y la informacion necesaria para crear nuevas cuentas
export const obtenerInfoCuentasBancarias = async () => {
  try {
    const {
      data: { resultado: cuentasUsuario },
    } = await api.get<RespuestaApi<CuentaGiro[]>>(CUENTAS);
    // Ordeno las cuentas por la prop predeterminada

    const {
      data: { resultado: tiposCuenta },
    } = await api.get<RespuestaApi<TipoCuenta[]>>(TIPO_CUENTAS_COMBO);

    const {
      data: { resultado: comboBancos },
    } = await api.get<RespuestaApi<TipoCuenta[]>>(COMBO_BANCOS);

    return { cuentasUsuario, tiposCuenta, comboBancos };
  } catch {
    mostrarAlertaError(
      'Hubo un error al obtener las cuentas bancarias',
      'Alerta'
    );
  }
};

export const obtenerInfoCuentasMantenedor = createAsyncThunk(
  'mantenedor/obtener-info',
  async () => {
    const { comboBancos, cuentasUsuario, tiposCuenta } =
      await obtenerInfoCuentasBancarias();

    return {
      cuentasUsuario,
      tiposCuenta,
      comboBancos: crearComboSeleccion(comboBancos, 'nombre', 'id'),
    };
  }
);

const mantenedorCuentasSlice = createSlice({
  name: 'infoCuentasGiro',
  initialState: estadoInicial,
  reducers: {
    editarCuentaGiroMantenedor: (
      { cuentasUsuario },
      { payload }: PayloadAction<CuentaGiro>
    ) => {
      const cuentaAModificar = cuentasUsuario.findIndex(
        (c) => c.id === payload.id
      );

      // Si la cuenta actualizada es predeterminada
      if (payload.predeterminada) {
        // Si es el index 0 quiere decir que estoy modificando la predeterminada y no hay necesidad de modificar la matriz
        if (cuentaAModificar === 0) {
          cuentasUsuario[cuentaAModificar] = { ...payload };
        } else {
          // Modifico el index 0, porque la predeterminada siempre va a ser la primera de la lista
          cuentasUsuario[0] = {
            ...cuentasUsuario[0],
            predeterminada: false,
          };

          // Elimino su posicion antigua
          cuentasUsuario.splice(cuentaAModificar, 1);

          // Lo pongo al principio de la lista, porque es predeterminado
          cuentasUsuario.unshift(payload);
        }
      } else {
        cuentasUsuario[cuentaAModificar] = { ...payload };
      }
    },
    agregarCuentaGiroMantenedor: (
      { cuentasUsuario },
      { payload }: PayloadAction<CuentaGiro>
    ) => {
      if (payload.predeterminada) {
        cuentasUsuario[0].predeterminada = false;
        cuentasUsuario.unshift(payload);
      } else {
        cuentasUsuario.push(payload);
      }
    },
    eliminarCuentaGiroMantenedor: (
      state,
      { payload }: PayloadAction<CuentaGiro['id']>
    ) => {
      const nuevoEstado = state?.cuentasUsuario?.filter(
        (c) => c.id !== payload
      );
      state.cuentasUsuario = nuevoEstado;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(obtenerInfoCuentasMantenedor.pending, (state) => {
        state.estado = CARGANDO;
      })
      .addCase(obtenerInfoCuentasMantenedor.fulfilled, (state, { payload }) => {
        return { ...state, ...payload, estado: ACTIVO };
      });
  },
});

export const {
  agregarCuentaGiroMantenedor,
  eliminarCuentaGiroMantenedor,
  editarCuentaGiroMantenedor,
} = mantenedorCuentasSlice.actions;

export default mantenedorCuentasSlice.reducer;
